import { getServiceTileTestId } from "@bcc/components/onboarding/service-tile.utils";
import { SHOP_DOMAIN_MODAL_TEST_IDS } from "@bcc/components/onboarding/shop-domain-modal.utils";
import { setUserNavTestId, USER_NAV_MULTILINGUAL_ATTRIBUTE } from "@bcc/components/user-nav.utils";
import { setSelectContentTestId } from "@bcc/components/ui/select/select.utils";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { TOUR_IDS } from "../../tour-ids";
import type { FeatureTour } from "../../tour.types";
import { testIdSelector, tourTargetSelector } from "../../tour.utils";

/** Every onboarding path counts as the step's page, so the engine never navigates while the roaster moves through the flow */
export const ONBOARDING_ROUTE_PATTERNS = [`${ROUTE_PATHS.ONBOARDING}/*`];

const onPage = { route: ROUTE_PATHS.ONBOARDING, alternateRoutePatterns: ONBOARDING_ROUTE_PATTERNS };

const USER_MENU_CONTENT = testIdSelector(setUserNavTestId("content"));
/** The avatar carries this only for roasters who can switch language, so the profile step is skipped for everyone else */
const PROFILE_MENU_WITH_LANGUAGES = `${tourTargetSelector(TOUR_IDS.profile.userNav)}[${USER_NAV_MULTILINGUAL_ATTRIBUTE}]`;
const MODAL = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.container);
/** Only the Shopify form renders the auth-method select (in both of its phases) */
const SHOPIFY_FORM = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.authMethodSelect);
const SHOPIFY_SHOP_DOMAIN = tourTargetSelector(TOUR_IDS.onboarding.shopifyShopDomain);
/** Only the WooCommerce form renders this wrapper */
const WOOCOMMERCE_FIELDS = tourTargetSelector(TOUR_IDS.onboarding.wooCommerceFields);
/** Mounts only once a Shopify auth method is chosen */
const CONNECT_BUTTON = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.connectButton);
/** Open dropdown lists are portalled outside the dialog; spotlight them too */
const SELECT_LIST = testIdSelector(setSelectContentTestId("select"));
const HELP_PANEL = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.helpPanel);
const CLIENT_ID_INPUT = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.clientIdInput);
const ACCESS_TOKEN_INPUT = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.accessTokenInput);
/** Shopify offers a language dropdown; WooCommerce a plain language field */
const SHOPIFY_LANGUAGE_SELECT = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.languageSelect);
const WOOCOMMERCE_LANGUAGE_INPUT = testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.languageInput);
/** The shop input's test id is shared with Shopify, so scope it to the WooCommerce wrapper */
const WOOCOMMERCE_SHOP_INPUT = `${WOOCOMMERCE_FIELDS} ${testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.shopInput)}`;

/**
 * Connect-store guide on /onboarding. Every step lets clicks through; the tile and auth-method
 * steps advance on their own, and each branch is gated on an element only that form renders.
 */
export const setupConnectTour: FeatureTour = {
  id: "setupConnect",
  titleKey: "tour.features.setupConnect.title",
  order: 1,
  steps: [
    {
      id: "setupConnect.profile",
      ...onPage,
      target: PROFILE_MENU_WITH_LANGUAGES,
      // The menu is opened for the roaster, who can pick a language right away
      extendTargets: [USER_MENU_CONTENT],
      prepare: [{ clickTarget: PROFILE_MENU_WITH_LANGUAGES }],
      titleKey: "tour.steps.setupConnect.profile.title",
      bodyKey: "tour.steps.setupConnect.profile.body",
      hintKey: "tour.steps.setupConnect.profile.hint",
      placement: "bottom",
      interactive: true,
      skipUnless: PROFILE_MENU_WITH_LANGUAGES,
    },
    {
      id: "setupConnect.tiles",
      ...onPage,
      target: tourTargetSelector(TOUR_IDS.onboarding.serviceTiles),
      extendTargets: [testIdSelector(getServiceTileTestId("shopify")), testIdSelector(getServiceTileTestId("woocommerce"))],
      titleKey: "tour.steps.setupConnect.tiles.title",
      bodyKey: "tour.steps.setupConnect.tiles.body",
      hintKey: "tour.steps.setupConnect.tiles.hint",
      placement: "bottom",
      interactive: true,
      advanceWhen: MODAL,
    },
    {
      id: "setupConnect.shopify-auth-method",
      ...onPage,
      target: SHOPIFY_FORM,
      extendTargets: [SELECT_LIST],
      titleKey: "tour.steps.setupConnect.shopifyAuthMethod.title",
      bodyKey: "tour.steps.setupConnect.shopifyAuthMethod.body",
      hintKey: "tour.steps.setupConnect.shopifyAuthMethod.hint",
      placement: "right",
      skipUnless: SHOPIFY_FORM,
      interactive: true,
      advanceWhen: CONNECT_BUTTON,
    },
    {
      id: "setupConnect.shopify-help",
      ...onPage,
      target: HELP_PANEL,
      titleKey: "tour.steps.setupConnect.shopifyHelp.title",
      bodyKey: "tour.steps.setupConnect.shopifyHelp.body",
      placement: "right",
      interactive: true,
      skipUnless: HELP_PANEL,
    },
    {
      id: "setupConnect.shopify-shop-domain",
      ...onPage,
      // The shop input's test id is shared with WooCommerce, so anchor on the Shopify-only wrapper
      target: SHOPIFY_SHOP_DOMAIN,
      titleKey: "tour.steps.setupConnect.shopifyShopDomain.title",
      bodyKey: "tour.steps.setupConnect.shopifyShopDomain.body",
      placement: "right",
      interactive: true,
      skipUnless: SHOPIFY_SHOP_DOMAIN,
    },
    {
      id: "setupConnect.shopify-client-credentials",
      ...onPage,
      target: CLIENT_ID_INPUT,
      extendTargets: [testIdSelector(SHOP_DOMAIN_MODAL_TEST_IDS.clientSecretInput)],
      titleKey: "tour.steps.setupConnect.shopifyClientCredentials.title",
      bodyKey: "tour.steps.setupConnect.shopifyClientCredentials.body",
      placement: "right",
      interactive: true,
      skipUnless: CLIENT_ID_INPUT,
    },
    {
      id: "setupConnect.shopify-access-token",
      ...onPage,
      target: ACCESS_TOKEN_INPUT,
      titleKey: "tour.steps.setupConnect.shopifyAccessToken.title",
      bodyKey: "tour.steps.setupConnect.shopifyAccessToken.body",
      placement: "right",
      interactive: true,
      skipUnless: ACCESS_TOKEN_INPUT,
    },
    {
      id: "setupConnect.shopify-language",
      ...onPage,
      target: SHOPIFY_LANGUAGE_SELECT,
      extendTargets: [SELECT_LIST],
      titleKey: "tour.steps.setupConnect.language.title",
      bodyKey: "tour.steps.setupConnect.language.body",
      placement: "right",
      interactive: true,
      skipUnless: SHOPIFY_LANGUAGE_SELECT,
    },
    {
      id: "setupConnect.woocommerce-shop-domain",
      ...onPage,
      target: WOOCOMMERCE_SHOP_INPUT,
      titleKey: "tour.steps.setupConnect.woocommerceShopDomain.title",
      bodyKey: "tour.steps.setupConnect.woocommerceShopDomain.body",
      placement: "right",
      interactive: true,
      skipUnless: WOOCOMMERCE_SHOP_INPUT,
    },
    {
      id: "setupConnect.woocommerce-language",
      ...onPage,
      target: WOOCOMMERCE_LANGUAGE_INPUT,
      titleKey: "tour.steps.setupConnect.language.title",
      bodyKey: "tour.steps.setupConnect.language.body",
      placement: "right",
      interactive: true,
      skipUnless: WOOCOMMERCE_LANGUAGE_INPUT,
    },
    {
      id: "setupConnect.connect",
      ...onPage,
      target: MODAL,
      extendTargets: [CONNECT_BUTTON],
      titleKey: "tour.steps.setupConnect.connect.title",
      bodyKey: "tour.steps.setupConnect.connect.body",
      hintKey: "tour.steps.setupConnect.connect.hint",
      placement: "right",
      spotlightPadding: 0,
      interactive: true,
      // Cancelling the dialog removes the target: go back to the platform step rather than wait forever
      skipUnless: MODAL,
      restartAtStepId: "setupConnect.tiles",
    },
  ],
};
